import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, moneyAmount } from '../../stores/coreSession';
import { trackEvent } from '../../utils/analytics';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const FALLBACK_ASSETS = [
  { assetId: 'weed', streetName: 'WEED' },
  { assetId: 'quaaludes', streetName: 'LUDES' },
  { assetId: 'speed', streetName: 'SPEED' },
  { assetId: 'cocaine', streetName: 'COCAINE' },
];

const FALLBACK_DISTRICTS = [
  { districtId: 'bronx', name: 'The Bronx' },
  { districtId: 'precession_zone', name: 'Precession Zone' },
  { districtId: 'axioma_null', name: 'Axioma Null' },
];

const state = {
  assets: [],
  districts: FALLBACK_DISTRICTS,
  loading: false,
  loaded: false,
  error: null,
  pending: null,
  notice: '',
  dossier: 0,
};

let loadPromise = null;

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, String(value), x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function snapshot() {
  const session = useCoreSession.getState();
  const core = session.state || {};
  return {
    status: session.status,
    cash: moneyAmount(core.cash),
    debt: moneyAmount(core.debt),
    heat: Number(core.heat ?? 0),
    day: session.run?.day ?? core.day ?? null,
    districtId: core.districtId || 'bronx',
    inventory: core.inventory || [],
    runActive: Boolean(session.run?.sessionId),
    loading: Boolean(session.loading),
  };
}

function owned(assetId) {
  return Number(
    (useCoreSession.getState().state?.inventory || []).find(
      (item) => item.assetId === assetId,
    )?.quantity ?? 0,
  );
}

function short(value, length = 10) {
  return String(value || '--').toUpperCase().slice(0, length);
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function price(value) {
  return value?.unitPrice?.amount ? `$${value.unitPrice.amount}` : '--';
}

function deltaLabel(value) {
  const amount = value?.previousUnitPrice?.amount;
  if (!amount) return '--';
  const current = Number(value?.unitPrice?.amount ?? 0);
  const previous = Number(amount);
  if (!previous) return '--';
  const bps = Number(value?.priceDeltaBps ?? 0);
  const sign = bps > 0 ? '▲' : bps < 0 ? '▼' : '=';
  return `${sign}${Math.abs(bps / 100).toFixed(1)}%`;
}

function currentDistrictName() {
  const core = snapshot();
  return (
    state.districts.find((item) => item.districtId === core.districtId)?.name ||
    core.districtId ||
    'THE BRONX'
  );
}

function destinations() {
  const core = snapshot();
  return state.districts
    .filter((item) => item.districtId !== core.districtId)
    .slice(0, 2);
}

async function loadHome(force = false) {
  if (loadPromise) return loadPromise;
  if (state.loaded && !force) return null;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    try {
      const session = useCoreSession.getState();
      if (!session.token) return;
      await session.ensureSession();
      const content = await session.loadContent();
      const assets = (content?.assets || [])
        .filter((asset) => asset.market === 'public' && asset.tradable !== false)
        .map((asset) => ({
          assetId: asset.assetId,
          streetName: short(asset.streetName || asset.name || asset.assetId, 10),
        }));
      const districts = (content?.districts || [])
        .filter((district) => district.status === 'open')
        .map((district) => ({
          districtId: district.districtId,
          name: district.name || district.districtId,
        }));
      state.districts = districts.length ? districts : FALLBACK_DISTRICTS;
      const listed = assets.length ? assets : FALLBACK_ASSETS;
      state.assets = await Promise.all(
        listed.map(async (asset) => {
          const [buy, sell] = await Promise.all([
            session.quoteTrade(asset.assetId, 'buy', 1),
            session.quoteTrade(asset.assetId, 'sell', 1),
          ]);
          return { ...asset, buy, sell };
        }),
      );
      state.loaded = true;
      const fragments = await session.loadFragments().catch(() => []);
      state.dossier = Array.isArray(fragments) ? fragments.length : 0;
      await session.refreshState().catch(() => null);
    } catch (error) {
      state.error = coreErrorMessage(error);
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

function drawChip(ctx, x, y, label, value, id, onClick, active = false, mobile = false) {
  const width = (String(label).length + String(value).length + 4) * 8;
  ctx.fillStyle = TERMINAL.GREEN_DARKEST;
  ctx.fillRect(x, y, width, 18);
  ctx.strokeStyle = active ? TERMINAL.GREEN : TERMINAL.GREEN_DARK;
  ctx.strokeRect(x, y, width, 18);
  text(ctx, label, x + 4, y + 2, TERMINAL.TEXT_DIM);
  text(ctx, value, x + (String(label).length + 2) * 8, y + 2, active ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
  interactiveRegistry.register({
    id,
    x: x - 2,
    y: mobile ? y - 13 : y - 4,
    w: width + 4,
    h: mobile ? 44 : 26,
    skipInflation: true,
    onClick,
  });
  return width;
}

function drawStatus(ctx, safe) {
  const core = snapshot();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.width < 500 ? 50 : 30);
  rule(ctx, 0, safe.width < 500 ? 49 : 29, safe.width);
  const mobile = safe.width < 500;
  const title = `${short(currentDistrictName(), mobile ? 14 : 16)} // STREET BOARD`;
  text(ctx, title, 8, mobile ? 5 : 8, TERMINAL.TEXT);
  const metrics = mobile
    ? [
        ['D', core.day == null ? '--' : `${core.day}/30`, 'home-status-day'],
        ['$', String(Math.round(core.cash)), 'home-status-cash'],
        ['H', String(core.heat), 'home-status-heat'],
        ...(core.debt > 0 ? [['DBT', String(Math.round(core.debt)), 'home-debt']] : []),
      ]
    : [
        ['DAY', core.day == null ? '--' : `${core.day}/30`, 'home-status-day'],
        ['CASH', money(core.cash), 'home-status-cash'],
        ['HEAT', String(core.heat), 'home-status-heat'],
        ...(core.debt > 0 ? [['DEBT', money(core.debt), 'home-debt']] : []),
      ];
  if (mobile) {
    let x = 8;
    for (const [label, value, id] of metrics) {
      x += drawChip(ctx, x, 26, label, value, id, () => {
        if (id === 'home-debt') {
          navigateTo('vinnie');
        }
      }, id === 'home-debt', true) + 6;
    }
    return;
  }
  let x = safe.width - 8;
  for (const [label, value, id] of [...metrics].reverse()) {
    const width = (label.length + value.length + 4) * 8;
    x -= width;
    drawChip(ctx, x, 6, label, value, id, () => {
      if (id === 'home-debt') {
        trackEvent('loan_open', { source: 'home_debt_chip' });
        navigateTo('vinnie');
      }
    }, id === 'home-debt');
    x -= 6;
  }
}

function drawBoard(ctx, x, y, w, h, mobile) {
  const core = snapshot();
  DopeUI.Panel(ctx, x, y, w, h, 'COMMODITIES // QUICK TRADE');
  text(ctx, 'ASSET    OWN  BUY    SELL   Δ', x + 10, y + 24, TERMINAL.TEXT_DIM);
  rule(ctx, x + 8, y + 38, w - 16);
  const rowH = mobile ? 50 : 40;
  const buttonH = mobile ? 44 : 26;
  const top = y + 46;
  const list = state.assets.length ? state.assets : FALLBACK_ASSETS.map((asset) => ({ ...asset, buy: null, sell: null }));
  list.forEach((asset, index) => {
    const rowY = top + index * rowH;
    const buy = price(asset.buy);
    const sell = price(asset.sell);
    const delta = deltaLabel(asset.buy);
    const qty = owned(asset.assetId);
    const line = mobile
      ? `${short(asset.streetName, 8).padEnd(8)} ${buy.padStart(7)} ${sell.padStart(7)}`
      : `${short(asset.streetName, 8).padEnd(8)} x${String(qty).padEnd(2)} ${buy.padStart(7)} ${sell.padStart(7)} ${delta.padStart(5)}`;
    text(ctx, line, x + 10, rowY + (mobile ? 6 : 5), TERMINAL.TEXT);
    if (mobile) text(ctx, `OWN ${qty} // ${delta}`, x + 10, rowY + 25, TERMINAL.TEXT_DIM);
    const btnW = mobile ? 44 : 46;
    const btnX = x + w - btnW * 2 - 18;
    const btnY = rowY + (mobile ? 3 : 2);
    DopeUI.Button(ctx, btnX, btnY, btnW, buttonH, 'BUY', asset.buy?.canExecute ? 'hover' : 'normal');
    DopeUI.Button(ctx, btnX + btnW + 6, btnY, btnW, buttonH, 'SELL', asset.sell?.canExecute ? 'hover' : 'normal');
    interactiveRegistry.register({
      id: `home-buy-${asset.assetId}`,
      x: btnX,
      y: btnY,
      w: btnW,
      h: buttonH,
      skipInflation: true,
      onClick: () => {
        trackEvent('trade_start', { assetId: asset.assetId, side: 'buy', source: 'home_strip' });
        state.pending = { kind: 'trade', assetId: asset.assetId, side: 'buy', asset };
      },
    });
    interactiveRegistry.register({
      id: `home-sell-${asset.assetId}`,
      x: btnX + btnW + 6,
      y: btnY,
      w: btnW,
      h: buttonH,
      skipInflation: true,
      onClick: () => {
        trackEvent('trade_start', { assetId: asset.assetId, side: 'sell', source: 'home_strip' });
        state.pending = { kind: 'trade', assetId: asset.assetId, side: 'sell', asset };
      },
    });
  });
  const help = state.error
    ? state.error
    : state.loading
      ? 'CHECKING PRICES...'
      : state.notice || (core.debt > 0
        ? `NOTE ${money(core.debt)} // CONFIRM TRADE`
        : 'QUICK TRADE // CONFIRM BEFORE EXECUTION');
  text(ctx, help.slice(0, mobile ? 25 : 28), x + 10, y + h - 18, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
  const dossierLabel = `DOSSIER ${state.dossier}/4`;
  const dossierW = (dossierLabel.length + 2) * 8;
  text(ctx, dossierLabel, x + w - dossierW - 10, y + h - 18, TERMINAL.GREEN);
  interactiveRegistry.register({
    id: 'home-dossier',
    x: x + w - dossierW - 12,
    y: y + h - (mobile ? 30 : 24),
    w: dossierW + 4,
    h: mobile ? 44 : 24,
    skipInflation: true,
    onClick: () => navigateTo('hronir'),
  });
}

function drawNote(ctx, x, y, w, h, mobile) {
  const core = snapshot();
  DopeUI.Panel(ctx, x, y, w, h, 'THE NOTE');
  text(ctx, 'DEBT', x + 10, y + 28, TERMINAL.TEXT_DIM);
  text(ctx, money(core.debt), x + 82, y + 28, core.debt > 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
  text(ctx, 'CASH', x + 10, y + 46, TERMINAL.TEXT_DIM);
  text(ctx, money(core.cash), x + 82, y + 46, TERMINAL.TEXT);
  if (!mobile) text(ctx, '1.0% / TURN', x + 10, y + 64, TERMINAL.TEXT_DIM);
  const buttonH = mobile ? 44 : 28;
  const buttonY = mobile ? y + 64 : y + 80;
  DopeUI.Button(ctx, x + 8, buttonY, w - 16, buttonH, core.debt > 0 ? 'OPEN LEDGER' : 'CLEAN NOTE', 'normal');
  interactiveRegistry.register({
    id: 'home-vinnie',
    x: x + 8,
    y: buttonY,
    w: w - 16,
    h: buttonH,
    skipInflation: true,
    onClick: () => {
      navigateTo('vinnie');
    },
  });
}

function drawMoves(ctx, x, y, w, h, mobile) {
  const core = snapshot();
  DopeUI.Panel(ctx, x, y, w, h, 'MOVE ON // NEXT');
  const list = destinations();
  const gap = 6;
  const buttonH = mobile ? 44 : 32;
  const buttonW = (w - 20 - gap * (list.length - 1)) / Math.max(1, list.length);
  list.forEach((district, index) => {
    const bx = x + 10 + index * (buttonW + gap);
    DopeUI.Button(ctx, bx, y + 28, buttonW, buttonH, short(district.name, mobile ? 9 : 12), 'normal');
    interactiveRegistry.register({
      id: `home-move-${district.districtId}`,
      x: bx,
      y: y + 28,
      w: buttonW,
      h: buttonH,
      skipInflation: true,
      onClick: () => {
        state.pending = { kind: 'travel', districtId: district.districtId, name: district.name };
      },
    });
  });
  const routeY = y + h - buttonH - 8;
  DopeUI.Button(ctx, x + 10, routeY, w - 20, buttonH, 'ROUTE // ALL DISTRICTS', 'normal');
  interactiveRegistry.register({
    id: 'home-route',
    x: x + 10,
    y: routeY,
    w: w - 20,
    h: buttonH,
    skipInflation: true,
    onClick: () => navigateTo('route'),
  });
  if (!mobile) text(ctx, `TARIFF $25 // DAY ${core.day ?? '--'}`, x + 10, routeY - 12, TERMINAL.TEXT_DIM);
}

function drawDock(ctx, x, y, w, h, mobile) {
  const items = [
    ['VINNIE', 'vinnie', 'home-vinnie'],
    ['MARKET', 'market', 'home-market'],
    ['ROUTE', 'route', 'home-route'],
    ['APPS', 'apps', 'home-more'],
  ];
  const gap = 6;
  const buttonW = (w - gap * (items.length - 1)) / items.length;
  const buttonH = mobile ? 44 : 40;
  items.forEach(([label, view, id], index) => {
    const bx = x + index * (buttonW + gap);
    DopeUI.Button(ctx, bx, y, buttonW, buttonH, label, 'normal');
    interactiveRegistry.register({
      id,
      x: bx,
      y,
      w: buttonW,
      h: buttonH,
      skipInflation: true,
      onClick: () => navigateTo(view),
    });
  });
}

function drawConfirm(ctx, safe) {
  const pending = state.pending;
  if (!pending) return;
  const mobile = safe.width < 500;
  ctx.fillStyle = 'rgba(0,0,0,0.82)';
  ctx.fillRect(0, 0, safe.width, safe.height);
  const w = mobile ? safe.width - 32 : 360;
  const h = 150;
  const x = Math.round((safe.width - w) / 2);
  const y = Math.round((safe.height - h) / 2);
  DopeUI.Panel(ctx, x, y, w, h, pending.kind === 'travel' ? 'CONFIRM MOVE' : 'CONFIRM TRADE');
  const asset = pending.asset;
  const detail = pending.kind === 'travel'
    ? `${short(pending.name, 20)} // TARIFF $25.00`
    : `${pending.side.toUpperCase()} 1 ${short(asset?.streetName, 12)} @ ${price(pending.side === 'buy' ? asset?.buy : asset?.sell)}`;
  text(ctx, detail, x + 12, y + 32, TERMINAL.TEXT_BRIGHT);
  text(ctx, pending.kind === 'travel' ? 'DAY ADVANCES BY 1' : 'PRICE LOCKED // CONFIRM TO DEAL', x + 12, y + 52, TERMINAL.TEXT_DIM);
  const buttonH = mobile ? 44 : 32;
  const gap = 8;
  const buttonW = (w - 24 - gap) / 2;
  const buttonY = y + h - buttonH - 12;
  DopeUI.Button(ctx, x + 12, buttonY, buttonW, buttonH, 'CONFIRM', 'hover');
  DopeUI.Button(ctx, x + 12 + buttonW + gap, buttonY, buttonW, buttonH, 'CANCEL', 'normal');
  interactiveRegistry.register({
    id: 'home-confirm',
    x: x + 12,
    y: buttonY,
    w: buttonW,
    h: buttonH,
    skipInflation: true,
    onClick: async () => {
      if (state.loading) return;
      state.loading = true;
      try {
        const session = useCoreSession.getState();
        if (pending.kind === 'travel') {
          await session.executeTravel(pending.districtId);
          state.notice = `ARRIVED ${short(pending.name, 16)}`;
        } else {
          await session.executeTrade(pending.assetId, pending.side, 1);
          state.notice = `${pending.side.toUpperCase()} 1 ${short(pending.asset?.streetName, 10)}`;
        }
        state.pending = null;
        await loadHome(true);
      } catch (error) {
        state.error = coreErrorMessage(error);
        state.pending = null;
      } finally {
        state.loading = false;
      }
    },
  });
  interactiveRegistry.register({
    id: 'home-cancel',
    x: x + 12 + buttonW + gap,
    y: buttonY,
    w: buttonW,
    h: buttonH,
    skipInflation: true,
    onClick: () => {
      state.pending = null;
    },
  });
}

registerView('street_home', function renderStreetHome(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.14;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  const core = snapshot();
  const session = useCoreSession.getState();
  if (core.status === 'online' && session.token && !state.loaded && !state.loading) {
    loadHome();
  }

  const mobile = safe.width < 500;
  drawStatus(ctx, safe);
  if (mobile) {
    drawBoard(ctx, 8, 38, safe.width - 16, 276, true);
    drawNote(ctx, 8, 322, safe.width - 16, 116, true);
    drawMoves(ctx, 8, 442, safe.width - 16, 128, true);
    drawDock(ctx, 8, 576, safe.width - 16, 52, true);
  } else {
    drawBoard(ctx, 8, 36, 400, 250, false);
    drawNote(ctx, 416, 36, 216, 108, false);
    drawMoves(ctx, 416, 152, 216, 134, false);
    drawDock(ctx, 8, 296, 624, 52, false);
    const footerY = 356;
    text(ctx, state.notice || state.error || 'CLICK TO TRADE // VINNIE HOLDS THE NOTE', 8, footerY, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    text(ctx, 'DOPE CHAIN WARS // BRONX', safe.width - 216, 384, TERMINAL.GREEN_DARK);
  }
  drawConfirm(ctx, safe);
});

export default 'street_home';
